import {
	MilestoneErrorModal,
} from "@/containers/Project/components";
import { projectMilestoneType } from "@/propTypes";
import { useProjectContext } from "@/providers";
import {
	useCreateCTQSnapshotMutation,
	useGetCTQSnapshotQuery,
	useDeleteCTQSnapshotMutation,
} from "@/services";
import { Button, Col, Popconfirm, Radio, Row, Spin } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import {
	CtqSkuRadioGroup,
	CtqSkuStack,
	CtqSnapshotItem,
	CtqSnapshotLink,
	CtqSnapshotRow,
} from "./styled";

export const MilestonePanelCtqSnapshots = ({ milestone }) => {
	const intl = useIntl();
	const { projectId } = useParams();

	const {
		ctqTestPlans,
		ctqTestPlansLoading,
		ctqTestPlansSuccess,
		requestCtqTestPlans,
	} = useProjectContext();

	const { data: snapshotsData } = useGetCTQSnapshotQuery({
		routeParams: {
			projectId,
			milestoneId: milestone.id,
		},
	});
	const takenSkus = new Set(
		(snapshotsData ?? [])
			.map((snapshot) => snapshot.sku?.toLowerCase())
			.filter(Boolean)
	);
	const isSkuTaken = (testPlan) =>
		takenSkus.has(testPlan.sku?.toLowerCase()) ||
		(snapshotsData ?? []).some(
			(snapshot) => snapshot.testPlanId == testPlan.id
		);
	const hasAvailableSkus = Boolean(
		ctqTestPlans?.some((testPlan) => !isSkuTaken(testPlan))
	);
	const [createCTQSnapshot, { isLoading: isSaving }] =
		useCreateCTQSnapshotMutation();
	const [deleteCTQSnapshot, { isLoading }] = useDeleteCTQSnapshotMutation();

	const [selectedTestPlanId, setSelectedTestPlanId] = useState(null);

	const [errorModal, setErrorModal] = useState({
		visible: false,
		title: null,
		description: null,
		errors: [],
	});

	const handleCloseModal = () => {
		setErrorModal({
			visible: false,
			title: null,
			description: null,
			errors: [],
		});
	};

	const handleSave = async () => {
		const selectedTestPlan = ctqTestPlans?.find(
			(testPlan) => testPlan.id === selectedTestPlanId
		);

		if (!selectedTestPlan || isSkuTaken(selectedTestPlan)) {
			return;
		}

		try {
			await createCTQSnapshot({
				routeParams: {
					projectId,
					milestoneId: milestone.id,
				},
				body: {
					testPlanId: selectedTestPlan.id,
					sku: selectedTestPlan.sku,
				},
			}).unwrap();
			setSelectedTestPlanId(null);
		} catch {
			setErrorModal({
				visible: true,
				title: intl.formatMessage({
					id: "milestonesList.ctqSnapshotCreateErrorTitle",
				}),
				description: intl.formatMessage({
					id: "milestonesList.ctqSnapshotCreateErrorDescription",
				}),
				errors: [],
			});
		}
	};

	const handleDelete = async (snapshot) => {
		try {
			await deleteCTQSnapshot({
				routeParams: {
					projectId,
					milestoneId: milestone.id,
					snapshotId: snapshot.snapshotId,
				},
			}).unwrap();
		} catch {
			setErrorModal({
				visible: true,
				title: intl.formatMessage({
					id: "milestonesList.ctqSnapshotDeleteErrorTitle",
				}),
				description: intl.formatMessage({
					id: "milestonesList.ctqSnapshotDeleteErrorDescription",
				}),
				errors: [],
			});
		}
	};

	return (
		<Row>
			<Col className="pb-2">
				{!ctqTestPlansSuccess && !ctqTestPlansLoading && (
					<Row>
						<Button
							data-test-id="milestone-action-btn"
							onClick={requestCtqTestPlans}
							type="primary"
						>
							{intl.formatMessage({
								id: `milestonesList.getCtqSkus`,
							})}
						</Button>
					</Row>
				)}

				{ctqTestPlansLoading && <Spin />}

				{ctqTestPlansSuccess &&
					!ctqTestPlansLoading &&
					(ctqTestPlans?.length ? (
						<CtqSkuStack>
							<CtqSkuRadioGroup
								data-test-id="ctq-sku-radio-group"
								onChange={(e) => setSelectedTestPlanId(e.target.value)}
								value={selectedTestPlanId}
							>
								{ctqTestPlans.map((testPlan) => (
									<Radio
										key={testPlan.id}
										value={testPlan.id}
										disabled={isSkuTaken(testPlan)}
									>
										{testPlan.sku}
									</Radio>
								))}
							</CtqSkuRadioGroup>

							<Button
								data-test-id="milestone-save-ctq-snapshot-btn"
								type="primary"
								disabled={!selectedTestPlanId || !hasAvailableSkus}
								loading={isSaving}
								onClick={handleSave}
							>
								{intl.formatMessage({ id: "general.save" })}
							</Button>
						</CtqSkuStack>
					) : (
						<Row data-test-id="no-ctq-skus-available">
							{intl.formatMessage({
								id: "milestonesList.noCtqSkusAvailable",
							})}
						</Row>
					))}

				<CtqSnapshotRow>
					{snapshotsData?.map((snapshot) => (
						<CtqSnapshotItem key={snapshot.snapshotUrl}>
							<CtqSnapshotLink
								href={snapshot.snapshotUrl}
								data-test-id="milestone-ctq-snapshots"
								target="_blank"
								rel="noopener noreferrer"
							>
								{snapshot.snapshotUrlLabel}
							</CtqSnapshotLink>

							<Popconfirm
								title={intl.formatMessage({
									id: "milestonesList.ctqSnapshotDeleteConfirmTitle",
								})}
								description={intl.formatMessage({
									id: "milestonesList.ctqSnapshotDeleteConfirmDescription",
								})}
								okText={intl.formatMessage({ id: "general.yes" })}
								cancelText={intl.formatMessage({ id: "general.cancel" })}
								onConfirm={() => handleDelete(snapshot)}
								data-test-id="sku-delete-confirm"
							>
								<Button
									type="link"
									icon={<FontAwesomeIcon icon={faTrash} />}
									data-test-id="sku-delete-btn"
									loading={isLoading}
								/>
							</Popconfirm>
						</CtqSnapshotItem>
					))}
				</CtqSnapshotRow>

				<MilestoneErrorModal
					modalState={errorModal}
					closeModal={handleCloseModal}
				/>
			</Col>
		</Row>
	);
};

MilestonePanelCtqSnapshots.propTypes = {
	milestone: projectMilestoneType.isRequired,
};
